import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ConflictError } from '../../api/errors';
import { useAuth } from '../../auth/AuthContext';
import type { Site, SiteContactPatch } from '../../domain/inventory';
import { assertSiteSaved } from '../../domain/siteSaveCheck';
import { formatNoteDate } from '../../domain/siteNote';
import { formatPenCountsDate } from '../../domain/sitePenCounts';
import { siteCompleteness } from './site-completeness';
import { useSitePenCounts, useSiteStatusSummaries } from './site-status-data';

/**
 * A site at a glance (tidaltech/koi#8218, #8261): systems ordered vs
 * assigned, the last contact with the site and the latest status note.
 * Ordered systems are edited here; notes on the Status log tab.
 */
export function SiteStatusPanel({ site, onOpenLog }: { site: Site; onOpenLog: () => void }) {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { bySite } = useSiteStatusSummaries(site.customerId);
  const summary = bySite.get(site.id);
  const penCounts = useSitePenCounts(site.customerId);
  const pens = penCounts.bySite.get(site.id);
  const pensNote = pens ? (
    <small className="site-status-by"> on {formatPenCountsDate(pens.date)}</small>
  ) : null;
  const noFarmData = penCounts.query.isPending ? (
    '…'
  ) : (
    <span className="empty-note">No farm data</span>
  );

  // Systems the same way as the customer's site table: complete systems
  // (fewest of cameras, winches, control units), and how many are active.
  const items = useQuery({
    queryKey: ['items-for-site', site.id],
    queryFn: () => inventoryApi.listItems({ siteId: site.id, pageSize: 5000 }),
  });
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const systems =
    items.data && parts.data
      ? siteCompleteness(site, items.data.data, new Map(parts.data.map((part) => [part.id, part])))
      : undefined;

  const save = useMutation({
    mutationFn: async (value: number | null) => {
      const patch: SiteContactPatch = {
        id: site.id,
        customerId: site.customerId,
        version: site.version,
        ...(value === null ? { clear: ['orderedSystems'] } : { orderedSystems: value }),
      };
      // An OK alone doesn't mean it was kept: see SiteNotSavedError.
      return assertSiteSaved(patch, await inventoryApi.updateSiteContact(patch));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      setEditing(false);
    },
    onError: (failure: unknown) =>
      setError(
        failure instanceof ConflictError
          ? 'Someone else changed this site. Reload the page and try again.'
          : 'Ordered systems were not saved. Nothing was changed; please try again later.',
      ),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    const text = draft.trim();
    if (text === '') {
      save.mutate(null);
      return;
    }
    if (!/^\d+$/.test(text) || Number(text) > 1000) {
      setError('Enter a whole number from 0 to 1000, or leave it empty.');
      return;
    }
    save.mutate(Number(text));
  }

  return (
    <section className="site-status-panel" aria-label="Site status">
      <dl className="site-status-figures">
        <div>
          <dt>Ordered systems</dt>
          <dd>
            {editing ? (
              <form className="inline-edit" onSubmit={handleSubmit} aria-label="Ordered systems">
                <input
                  inputMode="numeric"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  aria-label="Ordered systems"
                  autoFocus
                />
                <button type="submit" className="primary" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {site.orderedSystems ?? <span className="empty-note">Not set</span>}{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setDraft(site.orderedSystems === undefined ? '' : String(site.orderedSystems));
                    setError(null);
                    setEditing(true);
                  }}
                  disabled={!canMutate}
                >
                  Edit<span className="visually-hidden"> ordered systems</span>
                </button>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Systems</dt>
          <dd title="Complete systems on the site: the fewest of cameras, winches and unit controllers/powerboxes">
            {systems ? (
              <>
                {systems.assembled}
                <small className="site-status-by"> · {systems.active} active</small>
              </>
            ) : (
              '…'
            )}
          </dd>
        </div>
        <div>
          <dt>Pens with fish</dt>
          <dd title="From the farm's data: pens with fish at the end of the day">
            {pens ? (
              <>
                {pens.pensWithFish} of {pens.pens}
                {pensNote}
              </>
            ) : (
              noFarmData
            )}
          </dd>
        </div>
        <div>
          <dt>Producing data</dt>
          <dd title="Pens our systems sent data from that day or the day before">
            {pens ? (
              <>
                {pens.pensWithData}
                {pensNote}
              </>
            ) : (
              noFarmData
            )}
          </dd>
        </div>
        <div>
          <dt>Last contact</dt>
          <dd>
            {summary?.lastContact ? (
              <>
                {formatNoteDate(summary.lastContact.createTime)}
                <span className="site-status-by"> · {summary.lastContact.author}</span>
              </>
            ) : (
              <span className="empty-note">None recorded</span>
            )}
          </dd>
        </div>
      </dl>
      {error ? (
        <p role="alert" className="form-error">
          {error}
        </p>
      ) : null}
      <p className="site-status-latest">
        {summary?.latest ? (
          <>
            <span className="site-status-by">
              {formatNoteDate(summary.latest.createTime)} · {summary.latest.author}:
            </span>{' '}
            {summary.latest.text}{' '}
          </>
        ) : (
          <span className="empty-note">No status notes yet. </span>
        )}
        <button type="button" className="link-button" onClick={onOpenLog}>
          {summary?.latest ? 'Status log' : 'Add a note'}
        </button>
      </p>
    </section>
  );
}
